import { CreateFleetingIdeaCommandState } from "./model/commands/fleeting_idea/create_fleeting_idea_command"
import { DeleteFleetingIdeaCommandState } from "./model/commands/fleeting_idea/delete_fleeting_idea_command"
import { ColourIdeasCommandState } from "./model/commands/idea/colour_ideas_command"
import { CreateIdeaCommandState } from "./model/commands/idea/create_idea_command"
import { DeleteIdeasCommandState } from "./model/commands/idea/delete_ideas_command"
import { MoveIdeasCommandState } from "./model/commands/idea/move_ideas_command"
import { SetIdeaFieldCommandState } from "./model/commands/idea/set_idea_field_command"
import { SetIdeaSourceCommandState } from "./model/commands/idea/set_idea_source_command"
import { ViewIdeaCommandState } from "./model/commands/idea/view_idea_command"
import { CreateProjectCommandState } from "./model/commands/projects/create_project_command"
import { DeleteProjectCommandState } from "./model/commands/projects/delete_project_command"
import { SetProjectFieldsCommandState } from "./model/commands/projects/set_project_fields_command"
import { CreateWebSourceCommandState } from "./model/commands/web_source/create_web_source_command"
import { DeleteWebSourceCommandState } from "./model/commands/web_source/delete_web_source_command"
import { SetWebSourceFieldCommandState } from "./model/commands/web_source/set_web_source_field_command"
import { SetWebSourceFieldsCommandState } from "./model/commands/web_source/set_web_source_fields_command"
import {
  ID,
  Location,
  WebSourceFields,
  Idea,
  EditableIdeaFields,
  WebSource,
  Project,
  EditableProjectFields,
  FleetingIdea,
  EditOriginator,
} from "./model/types"

const randomString = () => {
  return Math.random().toString(36).substring(2, 15)
}

interface Event<Type extends string> extends Object {
  type: Type
}

interface CommandEvent_CreateIdea
  extends Event<"command:create-idea">,
    CreateIdeaCommandState {}

interface CommandEvent_DeleteIdeas
  extends Event<"command:delete-ideas">,
    DeleteIdeasCommandState {}

interface CommandEvent_MoveIdeas
  extends Event<"command:move-ideas">,
    MoveIdeasCommandState {}

interface CommandEvent_ColourIdeas
  extends Event<"command:colour-ideas">,
    ColourIdeasCommandState {}

interface CommandEvent_ViewIdea extends Event<"command:view-idea">, ViewIdeaCommandState {}

interface CommandEvent_SetIdeaField
  extends Event<"command:set-idea-field">,
    SetIdeaFieldCommandState {}

interface CommandEvent_SetIdeaSource
  extends Event<"command:set-idea-source">,
    SetIdeaSourceCommandState {}

interface CommandEvent_CreateFleetingIdea
  extends Event<"command:create-fleeting-idea">,
    CreateFleetingIdeaCommandState {}

interface CommandEvent_DeleteFleetingIdea
  extends Event<"command:delete-fleeting-idea">,
    DeleteFleetingIdeaCommandState {}

interface CommandEvent_CreateWebSource
  extends Event<"command:create-web-source">,
    CreateWebSourceCommandState {}

interface CommandEvent_SetWebSourceField
  extends Event<"command:set-web-source-field">,
    SetWebSourceFieldCommandState {}

interface CommandEvent_SetWebSourceFields
  extends Event<"command:set-web-source-fields">,
    SetWebSourceFieldsCommandState {}

interface CommandEvent_DeleteWebSource
  extends Event<"command:delete-web-source">,
    DeleteWebSourceCommandState {}

interface CommandEvent_CreateProject
  extends Event<"command:create-project">,
    CreateProjectCommandState {}

interface CommandEvent_SetProjectFields
  extends Event<"command:set-project-fields">,
    SetProjectFieldsCommandState {}

interface CommandEvent_DeleteProject
  extends Event<"command:delete-project">,
    DeleteProjectCommandState {}

interface ModelEvent_InitialLoadComplete
  extends Event<"model:initial-load-complete"> {}

interface ModelEvent_IdeaCreated extends Event<"model:idea-created"> {
  id: ID
  idea: Idea
  originator: EditOriginator
}

interface ModelEvent_IdeaUpdated extends Event<"model:idea-updated"> {
  id: ID
  idea: Idea
  fields: EditableIdeaFields
  originator: EditOriginator
}

interface ModelEvent_IdeasDeleted extends Event<"model:ideas-deleted"> {
  ids: ID[]
  ideas: Idea[]
  originator: EditOriginator
}

interface ModelEvent_FleetingIdeaCreated
  extends Event<"model:fleeting-idea-created"> {
  id: ID
  fleetingIdea: FleetingIdea
  originator: EditOriginator
}

interface ModelEvent_FleetingIdeaDeleted
  extends Event<"model:fleeting-idea-deleted"> {
  id: ID
  fleetingIdea: FleetingIdea
  originator: EditOriginator
}

interface ModelEvent_WebSourceCreated extends Event<"model:source-created"> {
  id: ID
  source: WebSource
  originator: EditOriginator
}

interface ModelEvent_WebSourceUpdated extends Event<"model:source-updated"> {
  id: ID
  source: WebSource
  fields: WebSourceFields
  originator: EditOriginator
}

interface ModelEvent_WebSourceDeleted extends Event<"model:source-deleted"> {
  id: ID
  source: WebSource
  originator: EditOriginator
}

interface ModelEvent_ProjectCreated extends Event<"model:project-created"> {
  id: ID
  project: Project
  originator: EditOriginator
}

interface ModelEvent_ProjectUpdated extends Event<"model:project-updated"> {
  id: ID
  project: Project
  fields: EditableProjectFields
  originator: EditOriginator
}

interface ModelEvent_ProjectDeleted extends Event<"model:project-deleted"> {
  id: ID
  project: Project
  originator: EditOriginator
}

interface SyncEvent_Complete extends Event<"sync:complete"> {}
interface SyncEvent_OnlineStatus extends Event<"sync:online-status"> {
  online: boolean
  offlineAt: Date | null
  syncErrorCount: number
}

interface IdeaPane_Open extends Event<"idea-pane:open"> {
  ideaId: ID
}

interface IdeaPane_Close extends Event<"idea-pane:close"> {}

interface NavigationHistoryEvent_SetOpenLocation
  extends Event<"navigation-history:set-open-location"> {
  location: Location | null
  selectionPos: number
}

interface NavigationHistoryEvent_OpenLocationSet
  extends Event<"navigation-history:open-location-set"> {
  location: Location | null
  selectionPos: number
}

interface ModelEvent_IdeaConnectionsChanged
  extends Event<"model:idea-connections-changed"> {
  from: ID
  to: ID[]
}

export type CommandEvent =
  | CommandEvent_CreateIdea
  | CommandEvent_DeleteIdeas
  | CommandEvent_CreateFleetingIdea
  | CommandEvent_DeleteFleetingIdea
  | CommandEvent_SetIdeaField
  | CommandEvent_MoveIdeas
  | CommandEvent_ColourIdeas
  | CommandEvent_ViewIdea
  | CommandEvent_CreateWebSource
  | CommandEvent_SetIdeaSource
  | CommandEvent_DeleteWebSource
  | CommandEvent_SetWebSourceField
  | CommandEvent_SetWebSourceFields
  | CommandEvent_CreateProject
  | CommandEvent_SetProjectFields
  | CommandEvent_DeleteProject

export type Events =
  | CommandEvent
  | IdeaPane_Open
  | IdeaPane_Close
  | ModelEvent_InitialLoadComplete
  | ModelEvent_InitialLoadComplete
  | NavigationHistoryEvent_SetOpenLocation
  | NavigationHistoryEvent_OpenLocationSet
  | ModelEvent_IdeaConnectionsChanged
  | SyncEvent_Complete
  | SyncEvent_OnlineStatus
  | ModelEvent_IdeaCreated
  | ModelEvent_IdeaUpdated
  | ModelEvent_IdeasDeleted
  | ModelEvent_FleetingIdeaCreated
  | ModelEvent_FleetingIdeaDeleted
  | ModelEvent_WebSourceCreated
  | ModelEvent_WebSourceUpdated
  | ModelEvent_WebSourceDeleted
  | ModelEvent_ProjectCreated
  | ModelEvent_ProjectUpdated
  | ModelEvent_ProjectDeleted

class Hub {
  listeners: Map<string, Map<string, Function>>
  anyListeners: Map<string, Function>

  constructor() {
    this.listeners = new Map()
    this.anyListeners = new Map()
  }

  onAny<Type extends Events["type"]>(
    listener: (action: Type, event: Events) => void
  ): string {
    const key = randomString()
    this.anyListeners.set(key, listener)
    return key
  }

  on<Type extends Events["type"]>(
    action: Type,
    listener: (event: Extract<Events, { type: Type }>) => void
  ): string {
    if (!this.listeners.has(action)) this.listeners.set(action, new Map())
    const key = randomString()
    this.listeners.get(action)?.set(key, listener)
    return key
  }

  emit<Type extends Events["type"]>(
    action: Type,
    event: Omit<Extract<Events, { type: Type }>, "type">
  ): void {
    logMessage(action, event)
    if (this.listeners.has(action))
      for (const [key, listener] of this.listeners.get(action)!) {
        listener(event)
      }
    for (const [_, listener] of this.anyListeners) {
      listener(action, event)
    }
  }

  emitAll<Type extends Events["type"]>(
    events: [Type, Omit<Extract<Events, { type: Type }>, "type">][]
  ): void {
    events.forEach(([action, event]) => this.emit(action, event))
  }

  remove(key: string): void {
    for (const [_, map] of this.listeners) map.delete(key)
    this.anyListeners.delete(key)
  }

  createEvent<Type extends Events["type"]>(
    action: Type,
    event: Omit<Extract<Events, { type: Type }>, "type">
  ): [Type, Omit<Extract<Events, { type: Type }>, "type">] {
    return [action, event]
  }
}

function logMessage(action, event) {
  if (typeof process !== "undefined") return
  const bgColor = "#EEFFAA"
  console.log(
    `%c%s%c %s%c`,
    `background-color: ${bgColor}; font-weight: bold; color: #222; padding: 2px; padding-left: 4px; padding-right: 4px; border-radius: 2px;`,
    "HUB",
    `background-color: none; font-family: 'Menlo', Monospace; font-weight: normal; color: #00F; padding-right: 4px;`,
    action + " ".repeat(Math.max(30 - action.length, 5)),
    `background-color: none; font-family: sans; color: #000;`,
    event
  )
}

export type EventTuple<T> = T extends { type: infer Type }
  ? [Type, Omit<T, "type">]
  : never

export default Hub
